import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { AuthGate } from "@/knowmeno/AuthGate";
import { ProfileSync } from "@/knowmeno/ProfileSync";
import { ConsentGate } from "@/knowmeno/ConsentGate";
import { AnalyticsBootstrap } from "@/knowmeno/AnalyticsBootstrap";
import { installStorageShim } from "@/knowmeno/storageShim";
import KnowMenoApp from "@/knowmeno/KnowMenoApp";
import { useStripeReturnHandler } from "@/knowmeno/useStripeReturnHandler";
import { PaymentFailedBanner } from "@/knowmeno/PaymentFailedBanner";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { syncSubscriptionFromStripe } from "@/lib/billing.functions";

export const Route = createFileRoute("/app")({
  head: () => ({
    meta: [
      { title: "Your Dashboard — KnowMeno" },
      {
        name: "description",
        content:
          "Track your symptoms, mood, cycle, and HRT — and build an ongoing report your doctor will actually use.",
      },
      { property: "og:title", content: "Your Dashboard — KnowMeno" },
      {
        property: "og:description",
        content:
          "Log symptoms, mood, cycle, and HRT in 30 seconds a day. Build a clinical-grade report your doctor can act on.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://knowmeno.com/app" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AppPage,
});

function AppPage() {
  const [shimReady, setShimReady] = useState(false);
  useEffect(() => {
    installStorageShim();
    setShimReady(true);
  }, []);

  if (!shimReady) return null;

  return (
    <div className="knowmeno-app-shell" style={{ background: "#FAF7F5", minHeight: "100vh", padding: "0 16px" }}>
      <div className="knowmeno-app-scaler">
        <AuthGate>
          {(session) => (
            <ProfileSync session={session}>
              <StripeReturnGate userId={session.user.id}>
                <PaymentFailedBanner userId={session.user.id} />
                <PortalReturnAware />
                <ConsentGate userId={session.user.id}>
                  <AnalyticsBootstrap userId={session.user.id} />
                  <KnowMenoApp
                    sessionEmail={session.user.email ?? ""}
                    signOut={async () => {
                      await supabase.auth.signOut();
                    }}
                  />
                </ConsentGate>
              </StripeReturnGate>
            </ProfileSync>
          )}
        </AuthGate>
      </div>
      <style>{`
        @media (min-width: 900px) {
          .knowmeno-app-shell {
            padding: 0 !important;
            width: 100vw;
            min-height: 100dvh !important;
            overflow-x: hidden;
          }
          .knowmeno-app-scaler {
            width: calc(100vw / 1.25);
            min-height: calc(100dvh / 1.25);
            zoom: 1.25;
            margin: 0 auto;
          }
          .knowmeno-app-root {
            width: 100% !important;
            max-width: none !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            box-sizing: border-box;
          }
        }
      `}</style>

    </div>
  );
}


// Wraps the post-auth app shell. When the user is returning from Stripe
// Checkout (URL has ?upgrade=success / ?session_id=..., or a pending return
// is persisted in sessionStorage), this gate blocks rendering of children
// and shows a full-screen "Finalizing your subscription…" overlay until the
// sync from Stripe completes.
function StripeReturnGate({ userId, children }: { userId: string; children: ReactNode }) {
  const { status, errorKind, retry, dismiss } = useStripeReturnHandler(userId);

  if (status === "syncing") {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#FAF7F5",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          zIndex: 9999,
          fontFamily: "system-ui, sans-serif",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            border: "3px solid #E8D5DA",
            borderTopColor: "#8A2A6B",
            borderRadius: "50%",
            animation: "knowmeno-spin 0.9s linear infinite",
          }}
        />
        <div style={{ color: "#4B1F4F", fontSize: 16, fontWeight: 600 }}>
          Finalizing your subscription…
        </div>
        <div style={{ color: "#6E4B71", fontSize: 13, maxWidth: 320 }}>
          Hang tight — we're confirming your plan with our payment processor.
        </div>
        <style>{`@keyframes knowmeno-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <>
      <StripeReturnBanner status={status} errorKind={errorKind} retry={retry} dismiss={dismiss} />
      {children}
    </>
  );
}

function StripeReturnBanner({
  status,
  errorKind,
  retry,
  dismiss,
}: {
  status: ReturnType<typeof useStripeReturnHandler>["status"];
  errorKind: ReturnType<typeof useStripeReturnHandler>["errorKind"];
  retry: ReturnType<typeof useStripeReturnHandler>["retry"];
  dismiss: ReturnType<typeof useStripeReturnHandler>["dismiss"];
}) {
  if (status === "cancelled") {
    return (
      <div style={{ background: "#FAF7F5", color: "#6E4B71", borderRadius: 12, padding: "10px 14px", margin: "10px 0", fontSize: 13, textAlign: "center" }}>
        Checkout cancelled — no charge was made. You can upgrade to Plus anytime.
      </div>
    );
  }
  if (status === "auth_lost") {
    return (
      <div style={{ background: "#FFE4EA", color: "#8A1F1F", borderRadius: 12, padding: "12px 14px", margin: "10px 0", fontSize: 13, display: "flex", flexDirection: "column", gap: 8, alignItems: "center", textAlign: "center" }}>
        <div>Your session expired during checkout. Sign in again to activate your subscription.</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={async () => { try { await supabase.auth.signOut(); } catch {} window.location.href = "/sign-in"; }}
            style={{ background: "#8A1F1F", color: "#fff", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 13, cursor: "pointer" }}
          >
            Sign in
          </button>
          <button onClick={dismiss} style={{ background: "transparent", color: "#8A1F1F", border: "1px solid #8A1F1F", borderRadius: 8, padding: "6px 12px", fontSize: 13, cursor: "pointer" }}>
            Dismiss
          </button>
        </div>
      </div>
    );
  }
  if (status === "error") {
    const msg =
      errorKind === "verify"
        ? "We couldn't verify your checkout session. If you were charged, contact support and we'll sort it out."
        : "Subscription activation didn't complete. Try again, or contact support if this keeps happening.";
    return (
      <div style={{ background: "#FFE4EA", color: "#8A1F1F", borderRadius: 12, padding: "12px 14px", margin: "10px 0", fontSize: 13, display: "flex", flexDirection: "column", gap: 8, alignItems: "center", textAlign: "center" }}>
        <div>{msg}</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
          {errorKind !== "verify" && (
            <button onClick={retry} style={{ background: "#8A1F1F", color: "#fff", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 13, cursor: "pointer" }}>
              Try again
            </button>
          )}
          <a href="mailto:support@knowmeno.com" style={{ background: "transparent", color: "#8A1F1F", border: "1px solid #8A1F1F", borderRadius: 8, padding: "6px 12px", fontSize: 13, textDecoration: "none" }}>
            Contact support
          </a>
          <button onClick={dismiss} style={{ background: "transparent", color: "#8A1F1F", border: "1px solid #8A1F1F", borderRadius: 8, padding: "6px 12px", fontSize: 13, cursor: "pointer" }}>
            Dismiss
          </button>
        </div>
      </div>
    );
  }
  return null;
}

// When the user returns from Stripe Customer Portal, sync the subscription.
// If they cancelled (no longer premium / trialing), sign them out and bounce
// to the landing page with a farewell toast flag.
function PortalReturnAware() {
  const sync = useServerFn(syncSubscriptionFromStripe);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") !== "portal") return;
    params.delete("from");
    const next = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`;
    window.history.replaceState({}, "", next);
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData.session?.access_token;
        if (!accessToken) return;
        const result = await sync({ data: { accessToken } });
        if (!result?.isPremium) {
          try { window.sessionStorage.setItem("knowmeno_farewell", "1"); } catch {}
          try { await supabase.auth.signOut(); } catch {}
          window.location.href = "/";
        }
      } catch (e) {
        console.error("[portal-return] sync failed", e);
      }
    })();
  }, [sync]);
  return null;
}
